import config from '../config';

const API_URL = config.API_URL;

/**
 * Enregistre en base la liste des campagnes que le dashboard vient d'afficher.
 *
 * ## Pourquoi c'est nécessaire
 *
 * Les labels et les objectifs sont attachés à une ligne `Campaign` en base, pas aux données de la
 * régie. Sans cette synchronisation, le panneau « Labels et objectifs » s'ouvrirait sur une table
 * vide — le pire des échecs, parce qu'il ressemble à une panne plutôt qu'à une absence de donnée.
 *
 * ## Pourquoi un fichier à part
 *
 * La fonction vivait dans `App.js` en `const` de module non exporté. Les dashboards Awin et
 * ChatGPT Ads sont des fichiers séparés : ils ne pouvaient pas s'en servir, et leurs campagnes
 * n'arrivaient donc jamais en base.
 *
 * ## Elle n'échoue jamais bruyamment
 *
 * C'est une tâche de fond, déclenchée à l'affichage d'un dashboard. Une erreur ici ne doit pas
 * empêcher la lecture des chiffres : elle se journalise et s'arrête là.
 *
 * @param {string} token           Jeton d'authentification
 * @param {string} accountId       `sourceId` du compte (les tirets sont retirés ici)
 * @param {string} accountType     Type de `LibraryItem` : GOOGLE_ADS, FACEBOOK_ADS, AWIN…
 * @param {Array}  campaigns       Campagnes affichées, avec `id`/`campaignId` et `name`/`campaignName`
 */
export const synchroniserCampagnes = async (token, accountId, accountType, campaigns) => {
  try {
    if (!token || !accountId) {
      console.log('🔄 [CAMPAIGNS-SYNC] Compte ou jeton manquant, synchronisation ignorée');
      return;
    }
    if (!campaigns || !campaigns.length) {
      console.log('🔄 [CAMPAIGNS-SYNC] Aucune campagne à synchroniser');
      return;
    }

    const campaignsToSync = campaigns.map(c => ({
      campaignId: (c.id || c.campaignId || '').toString(),
      campaignName: c.name || c.campaignName || ''
    })).filter(c => c.campaignId && c.campaignName);

    if (campaignsToSync.length === 0) {
      console.log('🔄 [CAMPAIGNS-SYNC] Aucune campagne valide après filtrage');
      return;
    }

    const cleanAccountId = String(accountId).replace(/-/g, '');

    const response = await fetch(`${API_URL}/api/campaigns/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        accountId: cleanAccountId,
        accountType: accountType || 'GOOGLE_ADS',
        campaigns: campaignsToSync
      })
    });

    const result = await response.json();
    if (result.success) {
      console.log(`🔄 [CAMPAIGNS-SYNC] ✅ ${result.created} créées, ${result.updated} mises à jour pour ${accountType}`);
    } else {
      console.warn('🔄 [CAMPAIGNS-SYNC] ❌ Erreur API:', result.error);
    }
  } catch (error) {
    // Ignorer les erreurs de sync (non bloquant)
    console.warn('[CAMPAIGNS-SYNC] Erreur (non bloquant):', error.message);
  }
};

export default synchroniserCampagnes;
